import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Formik, Form, Field } from 'formik'
import * as Yup from 'yup'
import { toast } from 'react-toastify'
import { FaEnvelope, FaTruck, FaArrowLeft, FaShieldAlt } from 'react-icons/fa'
import '../../assets/scss/pages/Auth.scss'

const ForgotPasswordSchema = Yup.object().shape({
  email: Yup.string().email('Invalid email address').required('Email is required'),
})

const ForgotPassword = () => {
  const [sent, setSent] = useState(false)

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      await new Promise(resolve => setTimeout(resolve, 1500))
      setSent(true)
      toast.success('Reset instructions sent! Check your inbox.')
    } catch {
      toast.error('Failed to send reset link. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="lg-wrap">

      {/* ── LEFT BRAND PANEL ── */}
      <div className="lg-panel">
        <div className="lg-panel-top">
          <div className="lg-logo">
            <div className="lg-logo-icon"><FaTruck /></div>
            <div className="lg-logo-name">TradeSetu <span>Transport</span></div>
          </div>

          <h1 className="lg-headline">
            Secure<br /><em>account</em><br />recovery.
          </h1>
          <p className="lg-sub">
            We'll send a password reset link to your registered email.
            Your data stays safe and protected at all times.
          </p>

          <div className="lg-features">
            {[
              'Encrypted reset link',
              'Link expires in 15 minutes',
              'No account data exposed',
            ].map(f => (
              <div className="lg-feature" key={f}>
                <div className="lg-feature-dot">✓</div>
                <span className="lg-feature-text">{f}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="lg-panel-bottom">
          © {new Date().getFullYear()} TradeSetu · Transport Management Platform
        </div>
      </div>

      {/* ── RIGHT FORM PANEL ── */}
      <div className="lg-form-wrap">
        <div className="lg-card">

          {/* mobile logo */}
          <div className="lg-mobile-logo">
            <div className="lg-mobile-logo-icon"><FaTruck /></div>
            <div className="lg-mobile-logo-name">TradeSetu</div>
          </div>

          {sent ? (
            /* ── SUCCESS STATE ── */
            <div className="lg-fp-success">
              <div className="lg-fp-success-icon"><FaShieldAlt /></div>
              <div className="lg-card-top">
                <h2 className="lg-card-title">Check Your Email</h2>
                <p className="lg-card-sub">
                  Reset instructions have been sent. Please check your inbox and
                  follow the link to reset your password.
                </p>
              </div>
              <p className="lg-fp-note">
                Didn't receive it? Check your spam folder or&nbsp;
                <button className="lg-fp-resend" onClick={() => setSent(false)}>try again</button>.
              </p>
              <Link to="/login" className="lg-btn" style={{ display: 'flex', justifyContent: 'center', textDecoration: 'none', marginTop: '8px' }}>
                <FaArrowLeft style={{ marginRight: '8px', marginTop: '2px' }} /> Back to Sign In
              </Link>
            </div>
          ) : (
            /* ── FORM STATE ── */
            <>
              <div className="lg-card-top">
                <h2 className="lg-card-title">Forgot Password?</h2>
                <p className="lg-card-sub">Enter your registered email and we'll send you a reset link</p>
              </div>

              <Formik
                initialValues={{ email: '' }}
                validationSchema={ForgotPasswordSchema}
                onSubmit={handleSubmit}
              >
                {({ errors, touched, isSubmitting, isValid, dirty }) => (
                  <Form className="lg-form">

                    {/* Email */}
                    <div className="lg-field">
                      <label className="lg-label"><FaEnvelope /> Registered Email</label>
                      <div className="lg-input-wrap">
                        <FaEnvelope className="lg-input-icon" />
                        <Field
                          type="email"
                          name="email"
                          className={`lg-input ${errors.email && touched.email ? 'lg-input--error' : ''}`}
                          placeholder="you@company.com"
                          autoComplete="email"
                        />
                      </div>
                      {errors.email && touched.email && (
                        <span className="lg-field-error">{errors.email}</span>
                      )}
                    </div>

                    {/* Submit */}
                    <button
                      type="submit"
                      className="lg-btn"
                      disabled={isSubmitting || !isValid || !dirty}
                    >
                      {isSubmitting
                        ? <><span className="spinner-border spinner-border-sm" /> Sending…</>
                        : 'Send Reset Link'}
                    </button>

                    <div className="lg-footer">
                      <Link to="/login" className="lg-back-link">
                        <FaArrowLeft /> Back to Sign In
                      </Link>
                    </div>

                  </Form>
                )}
              </Formik>
            </>
          )}

        </div>
      </div>

    </div>
  )
}

export default ForgotPassword
